import { useTranslations } from '@i18n/useLanguage'
import { SectionHeader } from '@app/components/blocks/sectionHeader/SectionHeader'
import { pages } from '@app/util/links'
import { useQuery } from '@based/sdk/react'
import { useMemo } from 'react'
import type { InitiativeSpotlightModel } from 'functions/app/spotlight/list/listTypes'
import type { ListInitiativeModel } from 'functions/app/initiative/list'
import Column from '@app/components/layout/Column'
import { SpotlightInitiativeGrid } from '@app/components/blocks/spotlightInitiativeGrid/SpotlightInitiativeGrid'
import { Empty } from '@app/components/blocks/empty/Empty'
import FindLocationImage from '@app/assets/images/find-location.png'

/** Max initiatives shown in the landing discover spotlight grid (shared with query limits). */
export const LANDING_SPOTLIGHT_INITIATIVE_COUNT = 5

export const DiscoverSection = () => {
  const t = useTranslations().app
  const spotlightQuery = useQuery('list-spotlight', {
    entityType: 'initiative',
    limit: LANDING_SPOTLIGHT_INITIATIVE_COUNT,
  })
  const initiativeQuery = useQuery('list-initiative', {
    top: LANDING_SPOTLIGHT_INITIATIVE_COUNT,
    desc: true,
  })

  const { initiatives, isPrimaryByIndex } = useMemo(() => {
    const spotlights: InitiativeSpotlightModel[] = spotlightQuery.data || []

    const fallbackInitiatives: ListInitiativeModel[] =
      initiativeQuery.data || []

    const spotlighted = spotlights.map((s) => s.initiative)

    if (spotlighted.length === LANDING_SPOTLIGHT_INITIATIVE_COUNT) {
      return {
        initiatives: spotlighted,
        isPrimaryByIndex: spotlights.map((s) => Boolean(s.isPrimary)),
      }
    }

    const nonDuplicated = fallbackInitiatives.filter(
      (fi) => spotlighted.findIndex((i) => i.uuid === fi.uuid) === -1,
    )

    const merged = [...spotlighted, ...nonDuplicated].slice(
      0,
      LANDING_SPOTLIGHT_INITIATIVE_COUNT,
    )
    const isPrimaryByIndex = merged.map((_, i) =>
      i < spotlights.length ? Boolean(spotlights[i]?.isPrimary) : false,
    )
    return { initiatives: merged, isPrimaryByIndex }
  }, [spotlightQuery.data, initiativeQuery.data])

  return (
    <Column alignItems="stretch">
      <SectionHeader
        title={t.landingPage.discoverSection.title}
        subtitle={t.landingPage.discoverSection.subTitle}
        seeMore={t.landingPage.discoverSection.seeMore}
        toRoute={pages.initiatives}
      />
      {initiatives.length === 0 ? (
        <Empty
          title={t.landingPage.discoverSection.emptyTitle}
          imageSrc={FindLocationImage}
        />
      ) : (
        <SpotlightInitiativeGrid
          initiatives={initiatives}
          isPrimaryByIndex={isPrimaryByIndex}
          notSpecified={t.common.notSpecified}
          searchingForMembersLabel={
            t.common.initiativeStatus.searchingForMembers
          }
          notSearchingForMembersLabel={
            t.common.initiativeStatus.notSearchingForMembers
          }
        />
      )}
    </Column>
  )
}
